import React, { useMemo } from "react";

import { GoalBasedGoalTypeSchema } from "@fartherfinance/frontend/api/FinancialPlans/goals/types";
import { GoalBasedFinancialPlanSingleResponse } from "@fartherfinance/frontend/api/FinancialPlans/plan/types";

import BorderBox from "@src/sharedComponents/BorderBox/BorderBox";
import BorderBoxBody from "@src/sharedComponents/BorderBox/BorderBoxBody";
import BorderBoxHeader from "@src/sharedComponents/BorderBox/BorderBoxHeader";
import LinkButton from "@src/sharedComponents/LinkButton/LinkButton";
import Text from "@src/sharedComponents/Typography/Text";
import { pluralize } from "@src/utils/pluralize";
import toUSD from "@src/utils/toUSD";

import GoalRow from "./GoalRow/GoalRow";
import SkeletonGoalTable from "./SkeletonGoalTable/SkeletonGoalTable";

import styles from "./GoalsTable.module.css";

interface Props {
  viewButtonUrl: string;
  selectedGoalBasedFinancialPlan:
    | GoalBasedFinancialPlanSingleResponse
    | undefined;
  isLoading: boolean;
}

const GoalsTable: React.FC<Props> = ({
  viewButtonUrl,
  selectedGoalBasedFinancialPlan,
  isLoading,
}: Props) => {
  const [goals, retirementGoals] = useMemo(() => {
    if (selectedGoalBasedFinancialPlan === undefined) {
      return [[], []];
    }

    const goals = selectedGoalBasedFinancialPlan.goals.filter(
      (g) => g.goalType !== GoalBasedGoalTypeSchema.Enum.Retirement
    );

    const retirementGoals = selectedGoalBasedFinancialPlan.goals.filter(
      (g) => g.goalType === GoalBasedGoalTypeSchema.Enum.Retirement
    );

    return [goals, retirementGoals];
  }, [selectedGoalBasedFinancialPlan]);

  const goalsTotal = goals.length + retirementGoals.length;

  if (isLoading) {
    return <SkeletonGoalTable />;
  }

  return (
    <BorderBox className={styles.container}>
      <BorderBoxHeader
        className={styles.header}
        start={
          <div className={styles.flex}>
            <Text variant={"heading1"} color={"text"}>
              Goals
            </Text>

            <Text variant="body" color={"textSubtle"}>
              {pluralize(goalsTotal, "Goal", true)}
            </Text>
          </div>
        }
        end={
          <LinkButton
            variant={"text"}
            buttonType={"secondary"}
            text={goalsTotal > 0 ? "View" : "Add"}
            to={viewButtonUrl}
            sx={{ height: "32px" }}
          />
        }
      />

      <BorderBoxBody className={styles.body}>
        {goalsTotal === 0 ? (
          <>
            <GoalRow leftLabel="Your Retirement Age" rightLabel="--" />
            <GoalRow leftLabel="Spouse's Retirement Age" rightLabel="--" />
          </>
        ) : (
          <>
            {retirementGoals.map(({ name, startDate }) => (
              <GoalRow
                key={name}
                leftLabel={name}
                rightLabel={`${startDate?.value ?? ""} years old`}
              />
            ))}
            {goals.map(({ name, amount }) => (
              <GoalRow
                key={name}
                leftLabel={name}
                rightLabel={
                  amount !== undefined
                    ? toUSD(Number(amount), {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : "N/A"
                }
              />
            ))}
          </>
        )}
      </BorderBoxBody>
    </BorderBox>
  );
};

export default GoalsTable;
